import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { Smartphone, Share, PlusSquare, MoreVertical, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { usePWAInstall, isMobileDevice, isIOS } from '../lib/pwa';

const BANNER_KEY = 'ops_install_banner_seen';
const ICON = '/icons/ops-academy-180.png';

export function AddToHomeHelp({ open, onOpenChange }) {
  const ios = isIOS();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" aria-describedby="a2hs-desc" data-testid="a2hs-help">
        <DialogHeader>
          <DialogTitle className="text-[#003366] flex items-center gap-3"><img src={ICON} alt="" className="w-10 h-10 rounded-xl" /> Add Ops Academy</DialogTitle>
          <p id="a2hs-desc" className="text-sm text-slate-500 text-left">{ios ? 'iPhone needs two quick taps in Safari:' : 'Use your browser menu:'}</p>
        </DialogHeader>
        <ol className="space-y-3 text-sm text-slate-700">
          {ios ? (
            <>
              <li className="flex items-center gap-2">1. Tap <Share size={16} className="text-[#007AFF]" /> <b>Share</b> at the bottom of Safari</li>
              <li className="flex items-center gap-2">2. Choose <PlusSquare size={16} /> <b>Add to Home Screen</b>, then <b>Add</b></li>
              <li className="text-xs text-slate-400">Using Chrome on iPhone? Tap Share at the top right, then Add to Home Screen.</li>
            </>
          ) : (
            <>
              <li className="flex items-center gap-2">1. Tap <MoreVertical size={16} /> <b>menu</b> at the top right</li>
              <li className="flex items-center gap-2">2. Choose <b>Add to Home screen</b> or <b>Install app</b></li>
            </>
          )}
        </ol>
      </DialogContent>
    </Dialog>
  );
}

function useAddToHome() {
  const { canPrompt, installed, install } = usePWAInstall();
  const [help, setHelp] = useState(false);
  const add = async () => {
    if (!canPrompt) { setHelp(true); return; }
    const outcome = await install();
    if (outcome === 'accepted') toast.success('Ops Academy is being added to your home screen');
  };
  return { show: isMobileDevice() && !installed, add, help, setHelp };
}

export function AddToHomeButton({ className = '', testId = 'a2hs-btn', onDone }) {
  const { show, add, help, setHelp } = useAddToHome();
  if (!show) return null;
  return (
    <>
      <button type="button" onClick={() => { add(); onDone?.(); }} className={`inline-flex items-center justify-center gap-2 rounded-md border border-[#003366]/30 text-[#003366] text-sm font-medium px-4 py-2 transition-colors hover:bg-[#003366] hover:text-white ${className}`} data-testid={testId}>
        <Smartphone size={16} /> Add to Home Screen
      </button>
      <AddToHomeHelp open={help} onOpenChange={setHelp} />
    </>
  );
}

export function AddToHomeBanner() {
  const { pathname } = useLocation();
  const { show, add, help, setHelp } = useAddToHome();
  const [seen, setSeen] = useState(() => sessionStorage.getItem(BANNER_KEY) === '1');
  const dismiss = () => { sessionStorage.setItem(BANNER_KEY, '1'); setSeen(true); };
  const hidden = seen || !show || pathname.startsWith('/shorts') || pathname.startsWith('/sign');
  return (
    <>
      {!hidden && (
        <div className="fixed inset-x-3 z-40 rounded-2xl bg-[#002B47] text-white shadow-2xl p-3 flex items-center gap-3 animate-in slide-in-from-bottom-4 fade-in duration-300" style={{ bottom: 'max(0.75rem, env(safe-area-inset-bottom))' }} data-testid="a2hs-banner">
          <img src={ICON} alt="" className="w-11 h-11 rounded-xl shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold">Ops Academy</p>
            <p className="text-xs text-white/70">Add it to your home screen for one-tap access</p>
          </div>
          <button type="button" onClick={() => { dismiss(); add(); }} className="shrink-0 rounded-full bg-[#F37021] px-3.5 py-2 text-xs font-bold" data-testid="a2hs-banner-add">Add</button>
          <button type="button" onClick={dismiss} aria-label="Dismiss" className="shrink-0 p-1 text-white/60 hover:text-white" data-testid="a2hs-banner-dismiss"><X size={18} /></button>
        </div>
      )}
      <AddToHomeHelp open={help} onOpenChange={setHelp} />
    </>
  );
}
